import React, { useState } from 'react';
import { Sheet, Button, TextInput, Body } from '@iris/react-components';
import { normalizeZip } from '@/utilities/zipHelpers';

type ChangeStateModalProps = {
  text: string;
  isOpen: boolean;
  currentZip: string;
  onClose: () => void;
  onSave: (zip: string) => Promise<void>;
};

export default function ChangeStateModal({
  text,
  isOpen,
  currentZip,
  onClose,
  onSave,
}: ChangeStateModalProps) {
  const [zipInput, setZipInput] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleOpen = () => {
    setZipInput(currentZip);
    setValidationError('');
  };

  const handleClose = () => {
    onClose();
    setZipInput('');
    setValidationError('');
    setIsValidating(false);
  };

  const handleSave = async () => {
    const sanitizedZip = normalizeZip(zipInput);
    if (sanitizedZip.length !== 5) {
      setValidationError('Please enter a valid 5-digit ZIP code.');
      return;
    }

    setIsValidating(true);
    setValidationError('');

    try {
      await onSave(sanitizedZip);
      handleClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unable to validate ZIP code. Please try again.';
      setValidationError(message);
    } finally {
      setIsValidating(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      handleOpen();
    }
  }, [isOpen, currentZip]);

  // Auto-focus ZIP input after Sheet opens (Radix steals initial focus to dialog root).
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen]);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open: boolean) => !open && handleClose()}
      title="Edit Registration State"
      hasHeader
      desktopPosition="center"
      modal
      fixedButtons
      className="change-state-modal"
      footerContent={
        /* iris-override (token unavailable): Button lacks width/background/text-color props for this design variant; using iris:! overrides for modal footer styling */
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={zipInput.length !== 5 || isValidating}
          data-testid="save-state-button"
          className="change-state-modal-button iris:!w-full !px-3 !py-3 iris:!text-base iris:!bg-(--base-color-primary-600) iris:!text-white iris:active:!bg-(--base-color-primary-600)"
        >
          {isValidating ? 'Validating...' : 'Save State'}
        </Button>
      }
      data-testid="change-state-modal"
    >
      <Body size="md" as="p" className="change-state-modal-description">
        {text}
      </Body>
      <TextInput
        ref={inputRef}
        id="zip-input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        data-testid="modal-zip-input"
        label="Registration ZIP Code"
        placeholder="ZIP Code"
        value={zipInput}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setZipInput(normalizeZip(e.target.value));
          setValidationError('');
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter' && zipInput.length === 5 && !isValidating) {
            handleSave();
          }
        }}
        maxLength={5}
        disabled={isValidating}
        error={!!validationError}
        errorMessage={validationError}
        className="!text-left"
      />
    </Sheet>
  );
}
